import { deleteRequest } from 'Services/baseRequests/deleteRequest'
import { putRequest } from 'Services/baseRequests/putRequest'
import { getRequest } from 'Services/baseRequests/getRequest'
import { postRequest } from 'Services/baseRequests/postRequest'
import {
  RequestResponse,
  GetEligibleSpendApiResponse,
  GetEligibleSpendResponse,
  GetEligibleSpendPayload,
  AdminAccountDetailsApiResponse,
  AdminCheckPlaidTokenResponse
} from 'Services/apiResponseTypes'

import { CountryCode } from 'Utils/types/countries'
import { TransactionAccountInfo, TruelayerConnectionInfo } from 'Utils/types/user'

export const deleteUserStripeCustomer = async (payload: {
  userId: number
}): Promise<RequestResponse> => {
  const response = await deleteRequest<RequestResponse>(
    `/admin/employees/${payload.userId}/delete_stripe_account`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export type UpdateUserCountryPayload = {
  userId: number
  countryCode: CountryCode
}
export const updateUserCountry = async (
  payload: UpdateUserCountryPayload
): Promise<RequestResponse> => {
  const response = await putRequest<RequestResponse, { country_code: CountryCode }>(
    `/admin/employees/${payload.userId}/update_user_country`,
    { country_code: payload.countryCode }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export const getEligibleSpend = async (
  payload: GetEligibleSpendPayload
): Promise<GetEligibleSpendResponse> => {
  const { userId, benefitProgramId } = payload

  const response = await getRequest<GetEligibleSpendApiResponse>(
    `/admin/employees/${userId}/list_eligible_spend?benefit_program_id=${benefitProgramId}`
  )

  return {
    statusCode: response.statusCode,
    success: response.success ?? false,
    message: response.message ?? '',
    data:
      response.success && response.data
        ? {
            currentEligibleSpend: response.data.current_eligible_spend,
            previousEligibleSpend: response.data.previous_eligible_spend
          }
        : undefined
  }
}

export const resetConnectionRenewDate = async (payload: {
  userId: number
}): Promise<RequestResponse> => {
  const response = await putRequest<RequestResponse>(
    `/admin/employees/${payload.userId}/update_renew_date`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export const refreshTransactions = async (payload: {
  userId: number
}): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse>(
    `/users/${payload.userId}/refresh_transactions`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export type AdminAccountDetailsResponse = {
  transactionAccounts: TransactionAccountInfo[]
  truelayerConnections: TruelayerConnectionInfo[]
}

export const getAdminAccountDetails = async (
  userId: number
): Promise<RequestResponse<AdminAccountDetailsResponse>> => {
  const response = await getRequest<AdminAccountDetailsApiResponse>(
    `/admin/employees/${userId}/account_details`
  )

  return {
    statusCode: response.statusCode,
    success: response.success ?? false,
    message: response.message ?? '',
    data: response.data
      ? {
          transactionAccounts: (response.data.transaction_accounts_list ?? []).map((ta) => ({
            id: ta.id,
            accountId: ta.account_id,
            accountName: ta.account_name,
            institutionName: ta.institution_name,
            mask: ta.mask,
            subType: ta.sub_type,
            accountType: ta.account_type,
            createdAt: ta.created_at,
            authRequired: ta.auth_required,
            plaidTokens: (ta.plaid_tokens ?? []).map((pt) => ({
              status: pt.status,
              itemId: pt.plaid_response?.item_id ?? ''
            }))
          })),
          truelayerConnections: (response.data.truelayer_connections ?? []).map((tc) => ({
            id: tc.id,
            institutionName: tc.institution_name,
            renewBy: tc.renew_by,
            expiresAt: tc.expires_at,
            accounts: (tc.purchase_accounts ?? []).map((pa) => ({
              accountId: pa.account_id,
              accountType: pa.account_type,
              displayName: pa.display_name
            }))
          }))
        }
      : undefined
  }
}

export const checkPlaidTokenStatus = async (
  userId: number,
  transactionAccountId: number
): Promise<AdminCheckPlaidTokenResponse> => {
  const response = await postRequest<AdminCheckPlaidTokenResponse>(
    `/users/${userId}/transaction_accounts/${transactionAccountId}/check_plaid_token`
  )

  return {
    errors: response.errors ?? [],
    message: response.message ?? null
  }
}

export const forceTransactionAccountUpdateMode = async (
  userId: number,
  transactionAccountId: number
): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, { transaction_account_id: number }>(
    `/admin/employees/${userId}/force_update_mode`,
    { transaction_account_id: transactionAccountId }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}
